import {getFeatureCost, getPriceWithCurrency, getQuotePriceWithTaxes, getTotalPrice} from "../helpers";
import {CABINETS_ONLY, EDGE_GROUP} from "../../bvcomponents/kvComponent/core/constants";
import store from "../../store";
import {numberWithCommas} from "../priceFunctions";

const costcoTax = 6; // 6% from products only! (80% of price) skip taxes for labor (20% of price)
const costcoProductPercentage = 80; //

export const roundSubTotalPrice = (productPrice) => Math.floor(productPrice);
export const roundProductPrice = (productPrice) => Math.floor(productPrice);
export const roundLaborPrice = (productPrice) => Math.floor(productPrice);
export const roundTaxPrice = (productPrice) => Math.floor(productPrice);

const getCostcoPercents = (storeState) => {
  const {fabricatorTaxes} = storeState.fabricatorState;
  const calculateTax = (Array.isArray(fabricatorTaxes) && fabricatorTaxes[0]?.materialRate
    ? fabricatorTaxes[0].materialRate : costcoTax) / 100;
  const costcoPercent = (Array.isArray(fabricatorTaxes) && fabricatorTaxes[0]?.costcoPercent
    ? fabricatorTaxes[0].costcoPercent : costcoProductPercentage) / 100;
  return {calculateTax, costcoPercent}
}

const getCostcoCountertopsCartItems = (cartItems) => {
  return cartItems.filter(cartItem => {
    return cartItem.partId === "center_island_countertop"
      || cartItem.partId === "countertop"
      || (cartItem.partId === "backsplash" && (
        (cartItem.fromWizardGroupId === "18")
        || (cartItem.fromWizardGroupId === "12")
        || (cartItem.fromWizardGroupId === "11")
        || (cartItem.fromWizardGroupId === "187")
        || (cartItem.fromWizardGroupId === "196")
        || (cartItem.fromWizardGroupId === "206")
      ))
      || EDGE_GROUP.some(partId => cartItem.partId === partId)
  })
}

const getCabinetsCartItems = (cartItems) => {
  return cartItems.filter(cartItem => CABINETS_ONLY.some(partId => cartItem.partId === partId))
}

export const getCartItemsPrice = (cartItems) => {
  return cartItems.reduce((materialTotalCost, cartItem) => {
    materialTotalCost += getFeatureCost(cartItem.cartFeature, cartItem.quantity, cartItem.fromQtCategory);
    return materialTotalCost
  }, 0);
}

export const getCostcoShopCartCost = (cartItems) => {
  return roundSubTotalPrice(getCartItemsPrice(getCostcoCountertopsCartItems(cartItems)) * 0.1);
}

/**
 * @param forCartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of cabinets
 * */
const getQuoteTotal = (forCartItems = [], withTaxes = false) => {
  if (!forCartItems.length) {
    return 0;
  }
  const totalPrice = getTotalPrice(forCartItems[0].currentQuote);
  return withTaxes ? getQuotePriceWithTaxes(totalPrice) : totalPrice.materialPrice
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of cabinets
 * */
const getCabinetsTotal = (cartItems, withTaxes = false) => {
  return getQuoteTotal(getCabinetsCartItems(cartItems), withTaxes);
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of countertops
 * */
const getCountertopsTotal = (cartItems, withTaxes = false) => {
  return getQuoteTotal(getCostcoCountertopsCartItems(cartItems), withTaxes);
}

const getLineItemProperty = (fieldName, storeState) => {
  const {cartItems} = storeState.cartState;
  const {settings} = storeState.fabricatorState;

  const lastDigit = fieldName.slice(-1) - 1; // "1" -> 0

  const nameWithoutNumber = fieldName.slice(0, -1); // "mkdCabinetQuantity"

  const isCabinet = nameWithoutNumber.includes("Cabinet");

  let neededCartItems = isCabinet ? getCabinetsCartItems(cartItems) : getCostcoCountertopsCartItems(cartItems);
  if (!isCabinet) {
    neededCartItems = neededCartItems.sort((a,b) => b.partId?.includes("countertop") - a.partId?.includes("countertop"));
  }
  const cartItem = neededCartItems[lastDigit];
  if (!cartItem) {
    return null
  }
  const property = isCabinet ? nameWithoutNumber.replace("mkdCabinet", "") : nameWithoutNumber.replace("mkdCountertop", "");
  switch (property) {
    case "Quantity":
      // return cartItem.quantity;
      return 1
    case "Description":
      return cartItem.cartFeature.getFeatureName();
    case "ModelNumber":
      return cartItem.cartFeature.dbFeature.sku || "-";
    // case "UnitPrice": {
    //   const displayTotal = getFeatureCost(cartItem.cartFeature, 1, cartItem.fromQtCategory);
    //   return numberWithCommas(Math.floor(displayTotal))
    // }
    case "UnitPrice": {
      const displayTotal = getFeatureCost(cartItem.cartFeature, 1, cartItem.fromQtCategory);
      return getPriceWithCurrency(settings, numberWithCommas(Math.floor(displayTotal)))
    }
    case "Total": {
      const displayTotal = getFeatureCost(cartItem.cartFeature, cartItem.quantity, cartItem.fromQtCategory);
      return getPriceWithCurrency(settings, numberWithCommas(Math.floor(displayTotal)))
    }
    default:
      return null
  }
}

export const getCostcoContractField = (fieldName, storeState = store.getState()) => {
  const {cartItems} = storeState.cartState;
  const {calculateTax, costcoPercent} = getCostcoPercents(storeState);

  const lastDigit = fieldName.slice(-1);
  if (!isNaN(+lastDigit)) { // "mkdCabinetQuantity1..5" or "mkdCountertopDescription1..5", total 50 fields
    return getLineItemProperty(fieldName, storeState)
  }

  switch (fieldName) {
    case "mkdRefacingSubtotal": {
      return numberWithCommas(roundSubTotalPrice(getCabinetsTotal(cartItems)));
    }
    case "mkdCountertopSubtotal": {
      return numberWithCommas(roundSubTotalPrice(getCountertopsTotal(cartItems)));
    }
    case "mkdProduct": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      return numberWithCommas(roundProductPrice(productTotal));
    }
    case "mkdLabor": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = roundProductPrice((cabinetsTotal + countertopsTotal) * costcoPercent);
      const laborTotal = (cabinetsTotal + countertopsTotal) - productTotal;
      return numberWithCommas(roundLaborPrice(laborTotal));
    }
    case "mkdTax": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      const taxTotal = productTotal * calculateTax; // 6% from product
      return numberWithCommas(roundTaxPrice(taxTotal));
    }
    case "mkdContractPrice": { // main price
      // const totalPrice = getTotalPrice();
      // const grandTotal = getQuotePriceWithTaxes(totalPrice);
      // return numberWithCommas(grandTotal);
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      const laborTotal = (cabinetsTotal + countertopsTotal) - productTotal;
      const taxTotal = productTotal * calculateTax; // 6% from product
      const displayTotal = roundSubTotalPrice(productTotal + laborTotal + taxTotal)
      return numberWithCommas(displayTotal)
    }
    /** RIGHT COLUMN **/
    case "mkdRefacingProductTotal": // right column
    case "costcoCabinetSubtotal": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const productTotal = roundProductPrice(cabinetsTotal * costcoPercent);
      return numberWithCommas(productTotal);
    }
    case "mkdCountertopProductTotal": // right column
    case "costcoCountertopSubtotal": {
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = roundProductPrice(countertopsTotal * costcoPercent);
      return numberWithCommas(productTotal);
    }
    case "mkdRefacingTax": { // right column 2
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const productTotal = cabinetsTotal * costcoPercent;
      const taxTotal = roundTaxPrice(productTotal * calculateTax); // 6% from product
      return numberWithCommas(taxTotal);
    }
    case "mkdCountertopTax": { // right column 2
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = countertopsTotal * costcoPercent;
      const taxTotal = roundTaxPrice(productTotal * calculateTax); // 6% from product
      return numberWithCommas(taxTotal);
    }
    case "mkdShopCardValue": {
      return getCostcoShopCartCost(cartItems);
    }
    /** LINES 1 **/
    // case "costcoCountertopSubtotal": { // countertops list bottom line1
    //   return numberWithCommas(roundSubTotalPrice(getCountertopsTotal(cartItems)));
    // }
    case "costcoCountertopTax": {  // countertops list bottom line2
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = countertopsTotal * costcoPercent;
      const taxTotal = roundTaxPrice(productTotal * calculateTax); // 6% from product
      return numberWithCommas(taxTotal);
    }
    case "costcoCountertopTotal": { // countertops list bottom line3
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = countertopsTotal * costcoPercent;
      const taxTotal = productTotal * calculateTax; // 6% from product
      // const displayTotal = roundSubTotalPrice(countertopsTotal + taxTotal);
      const displayTotal = roundSubTotalPrice(productTotal + taxTotal);
      return numberWithCommas(displayTotal);
    }
    /** LINES 2 **/
    // case "costcoCabinetSubtotal": { // cabinets list bottom line1
    //   return numberWithCommas(roundSubTotalPrice(getCabinetsTotal(cartItems)));
    // }
    case "costcoCabinetTax": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const productTotal = cabinetsTotal * costcoPercent;
      const taxTotal = roundTaxPrice(productTotal * calculateTax); // 6% from product
      return numberWithCommas(taxTotal);
    }
    case "costcoCabinetTotal": { // cabinets list bottom line2
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const productTotal = cabinetsTotal * costcoPercent;
      const taxTotal = productTotal * calculateTax; // 6% from product
      // const displayTotal = roundSubTotalPrice(cabinetsTotal + taxTotal);
      const displayTotal = roundSubTotalPrice(productTotal + taxTotal);
      return numberWithCommas(displayTotal);
    }
    /** BOTTOM FIELDS **/
    case "crAmnt": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const displayTotal = roundProductPrice(cabinetsTotal * 0.1); // floor countertops subtotal * 0.1
      return numberWithCommas(displayTotal);
    }
    case "ccAmnt": {
      const countertopsTotal = getCountertopsTotal(cartItems);
      const displayTotal = roundProductPrice(countertopsTotal * 0.1); // floor refacing subtotal * 0.1
      return numberWithCommas(displayTotal);
    }
    default:
      return null;
  }
}

export const isCostcoContractField = (field) => {
  return field.name.startsWith("mkd") || field.name.startsWith("costco") || field.name.includes("Amnt")
}
